/**
 * API service for Broker Document Upload.
 * Endpoint: /api/broker/documents
 */
import axios from "axios";

const API_DOMAIN = process.env.REACT_APP_TPOC_API_DOMAIN;

/**
 * Fetch uploaded documents list.
 * GET /api/broker/documents?loanId={loanId}
 * Response: { documents: BrokerDocumentResponse[] }
 * @param {string} [loanId] - optional loan filter
 * @returns {Promise<Array>} array of document objects
 */
export const fetchUploadedDocuments = async (loanId) => {
  const params = loanId ? { loanId } : {};
  const response = await axios.get(`${API_DOMAIN}/api/broker/documents`, { params });
  return response.data.documents || [];
};

/**
 * Upload a single document file.
 * POST /api/broker/documents/upload/{loanId} (multipart/form-data)
 * @param {File} file - the file to upload
 * @param {string} accessToken - access token for authorization
 * @param {string} loanId - loan identifier (route param)
 * @param {string} [loanNumber] - optional loan number (unused, kept for compatibility)
 * @param {function} onProgress - callback with progress percentage (0-100)
 * @returns {Promise<object>} uploaded document record
 */
export const uploadDocument = async (file, accessToken, loanId, loanNumber, onProgress) => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await axios.post(
    `${API_DOMAIN}/api/broker/documents/upload/${loanId}`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percent);
        }
      },
    }
  );

  return response.data;
};

/**
 * Upload a document for a specific condition row.
 * Supports both endpoint shapes:
 * - /api/broker/conditions/upload/{loanId}/{conditionInternalId}/{conditionExternalId}
 * - /api/broker/conditions/upload/{loanId}/{conditionInternalId} (legacy)
 * @param {File} file - the file to upload
 * @param {string} accessToken - access token for authorization
 * @param {string} loanId - loan identifier
 * @param {string} conditionInternalId - internal condition identifier
 * @param {string|function} [conditionExternalId] - external condition identifier, or progress callback for legacy callers
 * @param {function} [onProgress] - callback with progress percentage (0-100)
 * @returns {Promise<object>} upload response
 */
export const uploadConditionDocument = async (
  file,
  accessToken,
  loanId,
  conditionExternalId,
  conditionId,
  onProgress
) => {
  let externalId = conditionExternalId;
  let progressCallback = onProgress;

  // Backward compatibility: old callers pass (file, token, loanId, conditionId, onProgress)
  if (typeof conditionExternalId === "function") {
    progressCallback = conditionExternalId;
    externalId = null;
  }

  const formData = new FormData();
  formData.append("file", file);

  const encodedLoanId = encodeURIComponent(loanId);
  const encodedConditionExternalId = encodeURIComponent(conditionExternalId);
  const encodedConditionId = encodeURIComponent(conditionId);
  const endpoint = externalId
    ? `${API_DOMAIN}/api/conditions/upload/${encodedLoanId}/${encodedConditionId}/${encodedConditionExternalId}`
    : `${API_DOMAIN}/api/conditions/upload/${encodedLoanId}/${encodedConditionId}`;

  const response = await axios.post(
    endpoint,
    formData,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && typeof progressCallback === "function") {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          progressCallback(percent);
        }
      },
    }
  );

  return response.data;
};

/**
 * Compute dashboard summary from documents array (derived client-side).
 * @param {Array} documents - current documents list
 * @returns {object} dashboard summary { processing, classified, failed, total }
 */
export const getDashboardSummary = (documents) => {
  const processing = documents.filter((d) => d.status === "Processing").length;
  const classified = documents.filter((d) => d.status === "Classified").length;
  const failed = documents.filter((d) => d.status === "Failed").length;
  return {
    processing,
    classified,
    failed,
    total: documents.length,
  };
};
